import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type PayrollRunStatus = "BORRADOR" | "APROBADA" | "PAGADA";

export const PAYROLL_RUN_STATUS_LABEL: Record<PayrollRunStatus, string> = {
  BORRADOR: "Borrador",
  APROBADA: "Aprobada",
  PAGADA: "Pagada",
};

export interface PayrollRunLineRecord {
  id: string;
  employeeId: string;
  baseSalary: string;
  bonuses: string;
  otherDeductions: string;
  healthDeduction: string;
  pensionDeduction: string;
  grossPay: string;
  netPay: string;
  employee?: { id: string; fullName: string; documentNumber: string; position: string | null } | null;
}

export interface PayrollRunRecord {
  id: string;
  periodLabel: string;
  startDate: string;
  endDate: string;
  status: PayrollRunStatus;
  approvedAt: string | null;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
  lines: PayrollRunLineRecord[];
  createdBy?: { id: string; fullName: string; email: string } | null;
}

export interface ListPayrollRunsFilters {
  page?: number;
  pageSize?: number;
  status?: PayrollRunStatus;
  search?: string;
}

export function usePayrollRuns(filters: ListPayrollRunsFilters = {}) {
  return useQuery({
    queryKey: ["payroll-runs", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PayrollRunRecord[]>>("/payroll/runs", { params: filters });
      return data;
    },
  });
}

export interface CreatePayrollRunPayload {
  periodLabel: string;
  startDate: string;
  endDate: string;
  notes?: string;
}

export function useCreatePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePayrollRunPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PayrollRunRecord>>("/payroll/runs", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["payroll-runs"] }),
  });
}

export function useApprovePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PayrollRunRecord>>(`/payroll/runs/${id}/approve`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["payroll-runs"] }),
  });
}

export function useMarkPayrollRunPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PayrollRunRecord>>(`/payroll/runs/${id}/mark-paid`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["payroll-runs"] }),
  });
}

export function useDeletePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/payroll/runs/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["payroll-runs"] }),
  });
}

export function payrollRunTotal(run: PayrollRunRecord): number {
  return run.lines.reduce((sum, line) => sum + Number(line.netPay || 0), 0);
}
